import { useMemo } from 'react';
import { Button } from './Button';
import { LockOnIcon } from './icons';

export interface GroupBonusCelebrationProps {
  /** t('ec2.group.celebration.title') — `Group complete — nice one.` */
  title: string;
  /** The completed group's name. */
  groupName: string;
  /** Bonus bucks paid. */
  amount: number;
  /** Accessible text for the amount, e.g. t('ec2.celebration.earned', {amount}). */
  amountLabel?: string;
  actionLabel?: string;
  onAction?: () => void;
  className?: string;
}

/**
 * Gold-heavy confetti layout for the money moment (docs/07 addendum): ≈2× gold
 * vs the other brand colors, deterministic for stable renders & tests.
 */
function confettiPieces(count: number) {
  const colors = [
    'var(--bucks)',
    'var(--primary)',
    'var(--bucks)',
    'var(--accent)',
    'var(--bucks)',
    'var(--fg)',
  ];
  const sizes = [6, 9, 12];
  return Array.from({ length: count }, (_, i) => {
    const golden = (i * 137.508) % 360;
    const rad = (golden * Math.PI) / 180;
    const dist = 60 + ((i * 53) % 70);
    return {
      color: colors[i % colors.length] as string,
      size: sizes[i % sizes.length] as number,
      x: Math.round(Math.cos(rad) * dist),
      y: Math.round(Math.sin(rad) * dist - 40),
      r: ((i * 97) % 360) - 180,
      delay: (i % 6) * 30,
    };
  });
}

/**
 * Group-bonus celebration (docs/07 addendum "Group-bonus celebration"): fires
 * AFTER the crossing quest's own celebration (§5: one celebration at a time).
 * Lock-on brackets flash --success, QR-module confetti bursts gold-heavy, the
 * bonus amount lands as the gradient numeral. Reduced motion (CSS): the snap
 * and confetti animations are disabled → static frame.
 */
export function GroupBonusCelebration({
  title,
  groupName,
  amount,
  amountLabel,
  actionLabel,
  onAction,
  className = '',
}: GroupBonusCelebrationProps) {
  const pieces = useMemo(() => confettiPieces(24), []);

  return (
    <div
      role="status"
      data-testid="ec-group-celebration"
      className={`relative flex flex-col items-center gap-4 overflow-hidden px-6 py-10 text-center ${className}`}
    >
      <div aria-hidden className="pointer-events-none absolute inset-0" data-testid="ec-confetti">
        {pieces.map((p, i) => (
          <span
            key={i}
            className="ec-confetti-piece absolute left-1/2 top-1/3 rounded-[3px]"
            style={{
              width: p.size,
              height: p.size,
              background: p.color,
              animationDelay: `${p.delay}ms`,
              ['--ec-confetti-x' as string]: `${p.x}px`,
              ['--ec-confetti-y' as string]: `${p.y}px`,
              ['--ec-confetti-r' as string]: `${p.r}deg`,
            }}
          />
        ))}
      </div>

      <span className="ec-bracket-snap text-success">
        <LockOnIcon size={56} aria-hidden />
      </span>

      <h2 className="font-display text-3xl leading-[1.1] font-bold tracking-[-0.02em] text-fg">{title}</h2>
      <p className="max-w-[40ch] text-base text-fg-secondary">{groupName}</p>

      <p
        role="img"
        aria-label={amountLabel ?? `+${amount}`}
        className="font-display text-[44px] leading-none font-extrabold tabular-nums"
        style={{
          background: 'var(--gradient-bucks)',
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          color: 'transparent',
        }}
      >
        <span aria-hidden>+{amount}</span>
      </p>

      {actionLabel && onAction && (
        <Button variant="primary" onClick={onAction}>
          {actionLabel}
        </Button>
      )}
    </div>
  );
}
